import { useState, useEffect, type ReactElement } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { VariantProps } from 'class-variance-authority';
import { buttonVariants } from '@/components/ui/button';
import type { ComponentProps } from 'react';

/**
 * CopyToClipboardButton - A reusable button component that copies text to clipboard
 *
 * Supports two feedback modes:
 * - 'inline': Button changes appearance (icon + text) when copied
 * - 'external': Button stays same, exposes copied state via callback
 *
 * @example
 * ```tsx
 * // Inline mode (default)
 * <CopyToClipboardButton text="Hello world" />
 *
 * // External mode with success callback
 * <CopyToClipboardButton
 *   text={apiKey}
 *   feedbackMode="external"
 *   onCopySuccess={() => setShowMessage(true)}
 * />
 * ```
 */

type CopyToClipboardButtonBaseProps = ComponentProps<typeof Button> &
  VariantProps<typeof buttonVariants>;

export interface CopyToClipboardButtonProps extends CopyToClipboardButtonBaseProps {
  /**
   * The text to copy to clipboard (required)
   */
  text: string;

  /**
   * When true, success state shows only a check icon (no "Copied!" text).
   * Use inside tooltips or other tight layouts to avoid overflow.
   */
  compactCopiedState?: boolean;

  /**
   * Feedback mode: 'inline' changes button appearance, 'external' uses callbacks
   * @default 'inline'
   */
  feedbackMode?: 'inline' | 'external';

  /**
   * Callback fired when copy succeeds (useful for external mode)
   */
  onCopySuccess?: () => void;

  /**
   * Callback fired when copy fails
   */
  onCopyError?: (error: Error) => void;

  /**
   * Timeout duration in milliseconds before resetting copied state
   * @default 2000
   */
  timeout?: number;
}

export function CopyToClipboardButton({
  text,
  compactCopiedState = false,
  feedbackMode = 'inline',
  onCopySuccess,
  onCopyError,
  timeout = 2000,
  children,
  ...buttonProps
}: CopyToClipboardButtonProps): ReactElement {
  const { onClick: onButtonClick, ...restButtonProps } = buttonProps;
  const [copied, setCopied] = useState(false);

  // Reset copied state after timeout
  useEffect(() => {
    if (!copied) return;

    const timeoutId = setTimeout(() => {
      setCopied(false);
    }, timeout);

    return () => clearTimeout(timeoutId);
  }, [copied, timeout]);

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopySuccess?.();
    } catch (error) {
      const err =
        error instanceof Error
          ? error
          : new Error('Failed to copy to clipboard');
      console.error('Failed to copy to clipboard:', err);
      onCopyError?.(err);
    }
  }

  // Inline mode: button changes appearance
  if (feedbackMode === 'inline') {
    return (
      <Button
        type="button"
        onClick={e => {
          e.stopPropagation();
          onButtonClick?.(e);
          void handleCopy();
        }}
        {...restButtonProps}
      >
        {copied ? (
          compactCopiedState ? (
            <>
              <Check className="h-4 w-4" aria-hidden />
              <span className="sr-only">Copied</span>
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              Copied!
            </>
          )
        ) : (
          <>
            <Copy className="h-4 w-4" aria-hidden />
            {children || 'Copy to clipboard'}
          </>
        )}
      </Button>
    );
  }

  // External mode: button stays same, state exposed via callback
  return (
    <Button
      type="button"
      onClick={e => {
        e.stopPropagation();
        onButtonClick?.(e);
        void handleCopy();
      }}
      {...restButtonProps}
    >
      {children || 'Copy'}
    </Button>
  );
}

CopyToClipboardButton.displayName = 'CopyToClipboardButton';
